<script lang="ts">
	import { AppApiClient } from '../api_contracts/app.client';
	import type { DatabaseInfo, RepositoryInfo } from '../api_contracts/app';
	import { GrpcWebFetchTransport } from '@protobuf-ts/grpcweb-transport';
	import { API_URL } from '../utils/config';

	const transport = new GrpcWebFetchTransport({
		baseUrl: API_URL
	});
	const client = new AppApiClient(transport);

	let deployments = $state<any[]>([]);
	let loading = $state(true);
	let error = $state<string | null>(null);

	const token = localStorage.getItem('authToken') || '';

	// Fetch data on mount
	$effect(() => {
		fetchDeployments();
	});

	async function fetchDeployments() {
		try {
			loading = true;
			error = null;
			const items: any[] = [];

			// Fetch user's databases
			const dbResult = await client.getUserDatabases({ token });
			if (dbResult.response.successFull && dbResult.response.databases) {
				dbResult.response.databases.forEach((db: DatabaseInfo) => {
					items.push({
						id: db.id,
						project: db.name,
						type: 'database',
						environment: db.dbType?.toUpperCase() || 'DB',
						status: db.status === 'running' ? 'success' : db.status === 'stopped' ? 'failed' : 'deploying',
						deployedAt: getTimeAgo(db.createdAt),
						dbType: db.dbType,
						port: db.port
					});
				});
			}

			// Fetch user's repositories
			try {
				const repoResult = await client.getUserRepositories({ token });
				if (repoResult.response.successFull && repoResult.response.repositories) {
					repoResult.response.repositories.slice(0, 3).forEach((repo: RepositoryInfo) => {
						items.push({
							id: repo.id,
							project: repo.name,
							type: 'repository',
							environment: repo.isPrivate ? 'Private' : 'Public',
							status: 'success',
							branch: 'main',
							deployedAt: getTimeAgo(repo.createdAt),
							url: `#/repositories/${repo.owner}/${repo.name}`,
							owner: repo.owner
						});
					});
				}
			} catch (err) {
				console.log('No repositories found or endpoint not available');
			}

			deployments = items.sort((a, b) => {
				// Sort by time (most recent first)
				return 0; // Keep order as fetched
			});

		} catch (err) {
			error = 'Failed to load deployments';
			console.error('Error fetching deployments:', err);
		} finally {
			loading = false;
		}
	}

	function getTimeAgo(timestamp: Date | undefined): string {
		if (!timestamp) return 'unknown';

		const now = new Date();
		const itemDate = new Date(timestamp);
		const seconds = Math.floor((now.getTime() - itemDate.getTime()) / 1000);

		if (seconds < 60) return 'just now';
		if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
		if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
		if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
		return `${Math.floor(seconds / 604800)}w ago`;
	}

	function getStatusInfo(status: string) {
		switch (status) {
			case 'success':
				return { text: 'Active', color: 'bg-green-500', textColor: 'text-green-700 dark:text-green-400', bgColor: 'bg-green-50 dark:bg-green-900/20' };
			case 'deploying':
			case 'starting':
				return { text: 'Starting', color: 'bg-blue-500', textColor: 'text-blue-700 dark:text-blue-400', bgColor: 'bg-blue-50 dark:bg-blue-900/20' };
			case 'failed':
			case 'stopped':
				return { text: 'Stopped', color: 'bg-red-500', textColor: 'text-red-700 dark:text-red-400', bgColor: 'bg-red-50 dark:bg-red-900/20' };
			default:
				return { text: 'Unknown', color: 'bg-gray-500', textColor: 'text-gray-700 dark:text-gray-400', bgColor: 'bg-gray-50 dark:bg-gray-800' };
		}
	}

	function getTypeIcon(type: string) {
		if (type === 'database') {
			return `<svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
				<path d="M3 12v3c0 1.657 3.134 3 7 3s7-1.343 7-3v-3c0 1.657-3.134 3-7 3s-7-1.343-7-3z"/>
				<path d="M3 7v3c0 1.657 3.134 3 7 3s7-1.343 7-3V7c0 1.657-3.134 3-7 3S3 8.657 3 7z"/>
				<path d="M17 5c0 1.657-3.134 3-7 3S3 6.657 3 5s3.134-3 7-3 7 1.343 7 3z"/>
			</svg>`;
		}
		return `<svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
			<path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clip-rule="evenodd"/>
		</svg>`;
	}
</script>

<div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
	<div class="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
		<h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">Active Deployments</h2>
		<a href="#/deployments" class="text-sm text-indigo-600 dark:text-blue-400 hover:text-indigo-700 dark:hover:text-blue-300 font-medium">
			View all
		</a>
	</div>

	<div class="divide-y divide-gray-200 dark:divide-gray-700">
		{#if loading}
			<div class="p-12 text-center">
				<svg class="w-8 h-8 mx-auto text-gray-400 dark:text-gray-600 animate-spin" fill="none" viewBox="0 0 24 24">
					<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
					<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
				</svg>
				<p class="mt-2 text-sm text-gray-600 dark:text-gray-400">Loading...</p>
			</div>
		{:else if error}
			<div class="p-12 text-center">
				<svg class="w-8 h-8 mx-auto text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
				</svg>
				<p class="mt-2 text-sm text-gray-600 dark:text-gray-400">{error}</p>
			</div>
		{:else if deployments.length === 0}
			<div class="p-12 text-center">
				<svg class="w-12 h-12 mx-auto text-gray-400 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"/>
				</svg>
				<h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No active deployments</h3>
				<p class="mt-1 text-sm text-gray-600 dark:text-gray-400">Get started by creating a repository or database</p>
				<div class="mt-6 flex justify-center gap-3">
					<a
						href="#/repositories/new"
						class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
					>
						New Repository
					</a>
					<a
						href="#/databases/new"
						class="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
					>
						New Database
					</a>
				</div>
			</div>
		{:else}
			{#each deployments as deployment}
				<div class="p-6 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
					<div class="flex items-start justify-between">
						<div class="flex-1 min-w-0">
							<div class="flex items-center space-x-3 mb-2">
								<div class="flex items-center text-gray-500 dark:text-gray-400">
									{@html getTypeIcon(deployment.type)}
								</div>
								<h3 class="text-base font-semibold text-gray-900 dark:text-gray-100">
									{deployment.project}
								</h3>
								<span
									class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium {getStatusInfo(
										deployment.status
									).textColor} {getStatusInfo(deployment.status).bgColor}"
								>
									<span class="w-2 h-2 {getStatusInfo(deployment.status).color} rounded-full mr-1.5"></span>
									{getStatusInfo(deployment.status).text}
								</span>
								<span
									class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300"
								>
									{deployment.environment}
								</span>
							</div>

							<div class="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400 mb-2">
								{#if deployment.type === 'database'}
									<span class="flex items-center space-x-1">
										<svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
											<path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"/>
											<path fill-rule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm9.707 5.707a1 1 0 00-1.414-1.414L9 12.586l-1.293-1.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
										</svg>
										<span>Port: {deployment.port || 'N/A'}</span>
									</span>
									<span class="text-gray-400 dark:text-gray-500">•</span>
									<span>{deployment.deployedAt}</span>
								{:else}
									{#if deployment.owner}
										<span class="flex items-center space-x-1">
											<svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
												<path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd"/>
											</svg>
											<span>{deployment.owner}</span>
										</span>
										<span class="text-gray-400 dark:text-gray-500">•</span>
									{/if}
									{#if deployment.branch}
										<span>on {deployment.branch}</span>
										<span class="text-gray-400 dark:text-gray-500">•</span>
									{/if}
									<span>{deployment.deployedAt}</span>
								{/if}
							</div>

							{#if deployment.url && deployment.type === 'repository'}
								<a
									href={deployment.url}
									class="inline-flex items-center space-x-1 text-sm text-indigo-600 dark:text-blue-400 hover:text-indigo-700 dark:hover:text-blue-300"
								>
									<span>View repository</span>
									<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M9 5l7 7-7 7"
										/>
									</svg>
								</a>
							{/if}
						</div>
					</div>
				</div>
			{/each}
		{/if}
	</div>
</div>
